<template>
  <div class="img2video-container">
    <!-- 标题 -->
    <div class="title">图片合成视频工具</div>
    <!-- 描述 -->
    <div class="description">本工具可以将多张图片合成为一个视频，你可以选择音频文件并自定义视频总时长。每张图片显示时间为(总时长/图片数量)。<br>使用本工具时，请保证电脑运行流畅，视频生成大约20秒，请耐心等待</div>
    <div class="content">
      <!-- 左边设置板块 -->
      <div class="settings">
        <!-- 上传图片 -->
        <div class="upload-tip">【请上传图片】</div>
        <el-upload
          class="upload-container"
          action="#"
          multiple
          :on-change="handleFileChange"
          :on-preview="handlePictureCardPreview"
          :file-list="fileList"
          list-type="picture-card"
          :auto-upload="false"
          :on-remove="handleRemove"
          accept="image/*"
          draggable
        >
          <el-icon><Plus /></el-icon>
        </el-upload>
        <el-dialog v-model="dialogVisible">
          <img w-full :src="dialogImageUrl" alt="Preview Image" style="max-width: 100%;max-height: 100%;"/>
        </el-dialog>
        <!-- 选择音频文件 -->
        <div class="upload-tip">【请选择背景音乐】</div>
        <!-- 新增音频选择列表 -->
        <div class="audio-list">
          <div>
            <el-radio v-model="selectedAudioIndex" :label="-1">
              使用上传的音频
            </el-radio>
          </div>
          <div v-for="(audio, index) in audioList" :key="index">
            <el-radio v-model="selectedAudioIndex" :label="index">
              {{ audio.name }}
            </el-radio>
            <!-- 新增音频播放按钮 -->
            <audio :src="audio.path" controls style="height: 30px;"></audio>
          </div>
        </div>
        <el-upload
          class="audio-upload"
          action="#"
          :on-change="handleAudioChange"
          :auto-upload="false"
          :limit="1"
          accept="audio/*"
        >
          <el-button size="small">上传音频文件</el-button>
        </el-upload>
        <!-- 自定义视频总时长 -->
        <div class="upload-tip">【请选择视频时长】</div>
        <div class="slider-container">
          <span>视频总时长 (秒): {{ totalDuration }}</span>
          <el-slider
            v-model="totalDuration"
            :min="15"
            :max="20"
            :step="1"
            @change="updateTotalDuration"
          ></el-slider>
        </div>
        <!-- 生成视频按钮 -->
        <el-button
          type="primary"
          :loading="isGenerating"
          @click="generateVideo"
          :disabled="isGenerating"
        >生成视频</el-button>
      </div>
      <!-- 右边视频预览 -->
      <div class="preview">
        <video v-if="videoUrl" :src="videoUrl" controls></video>
        <el-button
          v-if="videoUrl"
          type="primary"
          @click="downloadVideo"
        >下载视频</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { Plus } from '@element-plus/icons-vue'
import 'element-plus/theme-chalk/display.css';
import { ElNotification } from 'element-plus';
// 存储选择的图片文件
const selectedFiles = ref([]);
// 存储选择的音频文件
const selectedAudio = ref(null);
// 存储生成的视频 URL
const videoUrl = ref(null);
// 视频名称
const videoName = 'output.mp4';
// 视频总时长
const totalDuration = ref(16);
// 是否正在生成视频
const isGenerating = ref(false);
// 上传的图片文件列表
const fileList = ref([]);
const dialogVisible = ref(false);
const dialogImageUrl = ref(null);

import bgm1 from '@/assets/bgm1.mp3';
import bgm2 from '@/assets/bgm2.wav';
import bgm3 from '@/assets/bgm3.mp3';
import bgm4 from '@/assets/bgm4.mp3';
// 新增音频列表
const audioList = ref([
  { name: '音乐1', path: bgm1 },
  { name: '音乐2', path: bgm2 },
  { name: '音乐3', path: bgm3 },
  { name: '音乐4', path: bgm4 },
]);
// 新增选中的音频索引
const selectedAudioIndex = ref(-1);

const handlePictureCardPreview = (file) => {
  dialogImageUrl.value = file.url;
  dialogVisible.value = true;
};

// 处理文件选择
const handleFileChange = (file, fileList) => {
  // 过滤出真正的 File 对象
  const validFiles = fileList.filter(item => {
    if (item.raw && item.raw instanceof File) {
      return item.raw;
    } else if (item instanceof File) {
      return item;
    }
    return false;
  }).map(item => item.raw || item);
  selectedFiles.value = validFiles;
};
// 处理音频文件选择
const handleAudioChange = (file) => {
  // 确保 file 是有效的 File 对象
  if (file && file.raw instanceof File) {
    selectedAudio.value = file.raw;
  } else {
    ElNotification({
      title: 'Error',
      message: '无效的音频文件',
      type: 'error',
    });
  }
};
// 处理移除文件
const handleRemove = (file, fileList) => {
  // 过滤出真正的 File 对象
  const validFiles = fileList.filter(item => {
    if (item.raw && item.raw instanceof File) {
      return item.raw;
    } else if (item instanceof File) {
      return item;
    }
    return false;
  }).map(item => item.raw || item);
  selectedFiles.value = validFiles;
};
// 更新视频总时长
const updateTotalDuration = (value) => {
  totalDuration.value = value;
};

// 播放音频

// 生成视频
// 生成视频
const generateVideo = async () => {
  if (isGenerating.value) return;
  if (selectedFiles.value.length === 0) {
    ElNotification({
      title: 'Error',
      message: '请选择至少一张图片',
      type: 'error',
    });
    return;
  }
  isGenerating.value = true;

  // 计算每张图片显示的时长
  const durationPerImage = Math.round((totalDuration.value * 1000) / selectedFiles.value.length); // 每张图片显示的时长（毫秒）

  // 创建 canvas 元素
  const canvas = document.createElement('canvas');
  canvas.width = 750; // 提高分辨率
  canvas.height = 1000; // 提高分辨率
  const ctx = canvas.getContext('2d');

  // 禁用图像平滑，避免画质压缩
  ctx.imageSmoothingEnabled = false;

  let audioStream = null;
  let audioContext = null;
  let source = null;
  let destination = null;
  let audio = null;

  if (selectedAudioIndex.value !== -1) {
    const selectedAudioPath = audioList.value[selectedAudioIndex.value].path;
    // 创建音频上下文
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    // 创建音频元素
    audio = document.createElement('audio');
    audio.src = selectedAudioPath;
    // 等待音频元数据加载完成
    await new Promise((resolve) => {
      audio.onloadedmetadata = resolve;
    });
    // 创建媒体元素源节点
    source = audioContext.createMediaElementSource(audio);
    // 创建媒体流目标节点
    destination = audioContext.createMediaStreamDestination();
    // 连接节点
    source.connect(destination);
    audioStream = destination.stream;

    // 播放音频
    audio.play().catch(() => {
      ElNotification({
        title: 'Error',
        message: '音频读取失败',
        type: 'error',
      });
    });
  } else if (selectedAudio.value) {
    // 确保 selectedAudio.value 是有效的 File 对象
    if (selectedAudio.value instanceof File) {
      // 创建音频上下文
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      // 创建音频元素
      audio = document.createElement('audio');
      audio.src = URL.createObjectURL(selectedAudio.value);
      // 等待音频元数据加载完成
      await new Promise((resolve) => {
        audio.onloadedmetadata = resolve;
      });
      // 创建媒体元素源节点
      source = audioContext.createMediaElementSource(audio);
      // 创建媒体流目标节点
      destination = audioContext.createMediaStreamDestination();
      // 连接节点
      source.connect(destination);
      audioStream = destination.stream;

      // 播放音频
      audio.play().catch(() => {
        ElNotification({
          title: 'Error',
          message: '音频读取失败',
          type: 'error',
        });
      });
    } else {
      ElNotification({
        title: 'Error',
        message: '无效的音频文件',
        type: 'error',
      });
    }
  }

  // 创建视频流
  const stream = canvas.captureStream();

  if (audioStream) {
    const tracks = audioStream.getAudioTracks();
    tracks.forEach(track => stream.addTrack(track));
  }

  // 检查浏览器是否支持 video/mp4，如果不支持则尝试 video/webm
  let mimeType = 'video/mp4';
  if (!MediaRecorder.isTypeSupported(mimeType)) {
    mimeType = 'video/webm';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      ElNotification({
        title: 'Error',
        message: '浏览器不支持 video/mp4 和 video/webm 格式',
        type: 'error',
      });
      isGenerating.value = false;
      return;
    }
  }

  const recorder = new MediaRecorder(stream, { mimeType: mimeType, audioBitsPerSecond: 128000, videoBitsPerSecond: 250000 });
  const chunks = [];

  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) {
      chunks.push(event.data);
    }
  };

  recorder.onstop = () => {
    const videoBlob = new Blob(chunks, { type: mimeType });
    videoUrl.value = URL.createObjectURL(videoBlob);
    // 释放音频上下文
    if (audioContext) {
      audioContext.close();
    }
    isGenerating.value = false;
  };

  // 开始录制
  recorder.start();

  // 预加载所有图片
  const images = await Promise.all(selectedFiles.value.map(file => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => resolve(img);
      img.onerror = reject;
    });
  }));

  // 计算图片在 canvas 上的缩放比例，保持原始比例
  const scales = images.map(img => Math.min(canvas.width / img.width, canvas.height / img.height));
  const xs = scales.map((scale, index) => (canvas.width - images[index].width * scale) / 2);
  const ys = scales.map((scale, index) => (canvas.height - images[index].height * scale) / 2);

  // 依次绘制图片，包括最后一张图片
  for (let i = 0; i < images.length; i++) {
    const img = images[i];
    const startTime = Date.now();
    const drawImage = () => {
      if (Date.now() - startTime < durationPerImage) {
        // 清空画布
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, xs[i], ys[i], img.width * scales[i], img.height * scales[i]);
        requestAnimationFrame(drawImage);
      }
    };
    drawImage();
    // 等待图片绘制完成
    await new Promise(resolve => setTimeout(resolve, durationPerImage));
  }

  // 停止录制
  recorder.stop();
};

// 下载视频
const downloadVideo = () => {
  const link = document.createElement('a');
  link.href = videoUrl.value;
  link.download = videoName;
  link.click();
};
</script>

<style scoped>
.img2video-container {
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.title {
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
}

.description {
  text-align: center;
  margin-bottom: 20px;
}

.content {
  display: flex;
  justify-content: space-between;
  width: 1000px;
}

.settings {
  width: 48%;
}

.preview {
  width: 48%;
}
</style>